import { ArrowRight, Bell, CheckCheck, ChevronDown, CreditCard, FileSignature, FileText, FolderOpen, MessageSquare, Mic, Send, ShieldCheck, Users } from 'lucide-react';
import type { AgentCapabilities } from '../lib/agent.js';
import { useAgentCopy, useAgentOperation } from '../lib/agentCopy.js';

/** The same manual-send journey shown in the first introduction step. */
export function AgentVoiceFlow({ compact = false }: { compact?: boolean }) {
    const c = useAgentCopy();
    const stages = [
        { icon: Mic, title: c('flowRecord'), body: c('flowRecordBody') },
        { icon: MessageSquare, title: c('flowSpeak'), body: c('flowSpeakBody') },
        { icon: Send, title: c('flowSend'), body: c('flowSendBody') },
        { icon: CheckCheck, title: c('flowResult'), body: c('flowResultBody') },
    ];
    return <ol className={`grid grid-cols-2 gap-3 ${compact ? 'sm:grid-cols-4' : 'lg:grid-cols-4'}`}>
        {stages.map(({ icon: Icon, title, body }, index) => <li key={title} className="relative min-w-0 rounded-2xl border border-border bg-card p-4">
            <div className="mb-4 flex items-center justify-between gap-2">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon size={22} aria-hidden="true" /></span>
                <span className="ltr-nums text-sm font-medium text-muted-foreground" aria-hidden="true">0{index + 1}</span>
            </div>
            <p className="font-semibold leading-relaxed">{title}</p>
            {!compact && <p className="mt-1 text-muted-foreground leading-relaxed">{body}</p>}
        </li>)}
    </ol>;
}

export function AgentCapabilityGroups({ capabilities }: { capabilities: AgentCapabilities }) {
    const c = useAgentCopy();
    const operation = useAgentOperation();
    const groups = [
        { prefix: 'quote.', title: c('groupQuote'), icon: FileText },
        { prefix: 'customer.', title: c('groupCustomer'), icon: Users },
        { prefix: 'inquiry.', title: c('groupInquiry'), icon: MessageSquare },
        { prefix: 'reminder.', title: c('groupReminder'), icon: Bell },
        { prefix: 'invoiceFolder.', title: c('groupFolder'), icon: FolderOpen },
        { prefix: 'signature.', title: c('groupSignature'), icon: FileSignature },
        { prefix: 'customerPayment.', title: c('groupPayment'), icon: CreditCard },
    ];
    return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {groups.map(({ prefix, title, icon: Icon }) => {
            const operations = capabilities.operations.filter(op => op.name.startsWith(prefix) || (prefix === 'invoiceFolder.' && op.name.startsWith('invoice.')));
            const first = operations[0];
            if (!first) return null;
            return <details key={prefix} className="group rounded-2xl border border-border bg-card open:bg-muted/30">
                <summary className="flex min-h-11 cursor-pointer list-none items-center gap-3 rounded-2xl p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon size={22} aria-hidden="true" /></span>
                    <span className="min-w-0 flex-1 leading-relaxed"><span className="block font-semibold">{title}</span><span className="mt-1 block text-base text-muted-foreground">{operation(first.name, first.description)}</span></span>
                    <ChevronDown size={18} className="shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
                </summary>
                <div className="space-y-3 border-t border-border px-4 py-4">
                    <ul className="space-y-2 text-base leading-relaxed">{operations.map(op => <li key={op.name} className="flex items-start gap-2">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                        <span>{operation(op.name, op.description)}</span>
                    </li>)}</ul>
                    {operations.some(op => op.external) && <p className="flex items-start gap-2 text-primary"><ShieldCheck size={18} className="mt-1 shrink-0" aria-hidden="true" />{c('externalApproval')}</p>}
                </div>
            </details>;
        })}
    </div>;
}

export function AgentRequestExample() {
    const c = useAgentCopy();
    return <div className="space-y-5 text-base leading-relaxed">
        <figure className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
            <figcaption className="mb-2 text-sm font-semibold text-primary">{c('exampleLabel')}</figcaption>
            <blockquote className="font-medium">{c('examplePrompt')}</blockquote>
        </figure>
        <div className="grid gap-4 sm:grid-cols-[1.4fr_1fr]">
            <section className="rounded-2xl border border-border bg-card p-5">
                <h4 className="mb-4 font-semibold">{c('dependentTitle')}</h4>
                <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 rounded-xl bg-muted px-3 py-2"><FileText size={20} className="text-primary" aria-hidden="true" />{c('exampleQuoteStep')}</span>
                    <ArrowRight size={20} className="shrink-0 text-muted-foreground rtl:rotate-180" aria-hidden="true" />
                    <span className="inline-flex items-center gap-2 rounded-xl bg-muted px-3 py-2"><FileSignature size={20} className="text-primary" aria-hidden="true" />{c('exampleSignatureStep')}</span>
                </div>
                <p className="mt-3 text-muted-foreground">{c('dependentBody')}</p>
            </section>
            <section className="rounded-2xl border border-border bg-card p-5">
                <div className="mb-3 flex items-center gap-2"><Bell size={20} className="shrink-0 text-primary" aria-hidden="true" /><h4 className="font-semibold">{c('independentTitle')}</h4></div>
                <p className="text-muted-foreground">{c('independentBody')}</p>
            </section>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
            <section className="rounded-2xl bg-muted/60 p-4"><h4 className="font-semibold">{c('missingTitle')}</h4><p className="mt-1 text-muted-foreground">{c('missingBody')}</p></section>
            <section className="rounded-2xl bg-muted/60 p-4"><div className="flex items-center gap-2"><ShieldCheck size={20} className="shrink-0 text-primary" aria-hidden="true" /><h4 className="font-semibold">{c('approvalTitle')}</h4></div><p className="mt-1 text-muted-foreground">{c('approvalBody')}</p></section>
        </div>
    </div>;
}

/** A compact, read-only overview for the agent's home page. */
export function AgentGuide() {
    const c = useAgentCopy();
    return <div className="space-y-4 text-base leading-relaxed"><AgentVoiceFlow compact /><p className="flex items-start gap-2 text-muted-foreground"><ShieldCheck size={20} className="mt-1 shrink-0 text-primary" aria-hidden="true" /><span>{c('multi')}</span></p></div>;
}
